import React, {
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import Spinner from '../../../Spinner';

function preventImageDrag(event) {
    event.preventDefault();
}

function useThumbnailStripController({
    mediaScans,
    selectedIndex,
    setVisibleThumbnailIds,
}) {
    const [stripNavigationState, setStripNavigationState] = useState({
        canScrollLeft: false,
        canScrollRight: false,
    });
    const [isStripCollapsed, setIsStripCollapsed] = useState(false);
    const stripRef = useRef(null);
    const thumbnailRefs = useRef({});

    useEffect(() => {
        const stripNode = stripRef.current;

        if (!stripNode || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                setVisibleThumbnailIds((currentVisibleIds) => {
                    const nextVisibleIdSet = new Set(currentVisibleIds);

                    entries.forEach((entry) => {
                        const { scanId } = entry.target.dataset;

                        if (!scanId) {
                            return;
                        }

                        if (entry.isIntersecting) {
                            nextVisibleIdSet.add(scanId);
                        } else {
                            nextVisibleIdSet.delete(scanId);
                        }
                    });

                    return mediaScans
                        .map((scan) => scan.id)
                        .filter((scanId) => nextVisibleIdSet.has(scanId));
                });
            },
            {
                root: stripNode,
                threshold: 0.6,
            },
        );

        Object.values(thumbnailRefs.current).forEach((node) => {
            observer.observe(node);
        });

        return () => {
            observer.disconnect();
        };
    }, [mediaScans, isStripCollapsed, setVisibleThumbnailIds]);

    useEffect(() => {
        const stripNode = stripRef.current;

        if (!stripNode) {
            return undefined;
        }

        const updateNavigationState = () => {
            const maxScrollLeft = Math.max(0, stripNode.scrollWidth - stripNode.clientWidth);

            setStripNavigationState({
                canScrollLeft: stripNode.scrollLeft > 1,
                canScrollRight: stripNode.scrollLeft < maxScrollLeft - 1,
            });
        };

        updateNavigationState();

        stripNode.addEventListener('scroll', updateNavigationState, { passive: true });
        window.addEventListener('resize', updateNavigationState);

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateNavigationState);
            resizeObserver.observe(stripNode);
        }

        return () => {
            stripNode.removeEventListener('scroll', updateNavigationState);
            window.removeEventListener('resize', updateNavigationState);

            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [mediaScans.length]);

    useEffect(() => {
        const activeScan = mediaScans[selectedIndex];
        const activeCard = activeScan ? thumbnailRefs.current[activeScan.id] : null;

        if (activeCard) {
            activeCard.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [mediaScans, selectedIndex]);

    const onStripWheel = (event) => {
        const stripNode = stripRef.current;

        if (!stripNode || stripNode.scrollWidth <= stripNode.clientWidth) {
            return;
        }

        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            event.preventDefault();
            stripNode.scrollLeft += event.deltaY;
        }
    };

    const scrollStrip = (direction) => {
        const stripNode = stripRef.current;

        if (!stripNode) {
            return;
        }

        const firstCard = stripNode.querySelector('.media-thumbnail-card');
        const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 180;
        const visibleCardCount = Math.max(1, Math.floor(stripNode.clientWidth / cardWidth));
        const scrollAmount = cardWidth * Math.max(1, visibleCardCount - 1);

        stripNode.scrollBy({
            left: direction === 'next' ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
        });
    };

    return {
        isStripCollapsed,
        onStripWheel,
        scrollStrip,
        setIsStripCollapsed,
        stripNavigationState,
        stripRef,
        thumbnailRefs,
    };
}

function MediaThumbnailStrip({
    mediaScans,
    selectedIndex,
    setSelectedIndex,
    setVisibleThumbnailIds,
    thumbnailStates,
}) {
    const {
        isStripCollapsed,
        onStripWheel,
        scrollStrip,
        setIsStripCollapsed,
        stripNavigationState,
        stripRef,
        thumbnailRefs,
    } = useThumbnailStripController({
        mediaScans,
        selectedIndex,
        setVisibleThumbnailIds,
    });

    return (
        <div className={`media-strip-shell ${isStripCollapsed ? 'collapsed' : ''}`}>
            <button
                type="button"
                className={`media-strip-collapse ${isStripCollapsed ? 'collapsed' : 'expanded'}`}
                onClick={() => setIsStripCollapsed((currentValue) => !currentValue)}
                aria-expanded={!isStripCollapsed}
                aria-label={isStripCollapsed ? 'Expand media thumbnail strip' : 'Collapse media thumbnail strip'}
                title={isStripCollapsed ? 'Show thumbnails' : 'Hide thumbnails'}
            >
                {isStripCollapsed ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
            </button>

            <div className={`media-strip-panel ${isStripCollapsed ? 'is-collapsed' : 'is-expanded'}`}>
                <div className="media-strip-panel-content">
                    {!isStripCollapsed && stripNavigationState.canScrollLeft && (
                        <button
                            type="button"
                            className="media-strip-nav previous"
                            onClick={() => scrollStrip('previous')}
                            aria-label="Show previous media thumbnails"
                        >
                            <span className="media-strip-nav-fade" aria-hidden="true" />
                            <span className="media-strip-nav-icon" aria-hidden="true">
                                <ArrowBackIosNewRoundedIcon />
                            </span>
                        </button>
                    )}

                    <div
                        ref={stripRef}
                        className="media-thumbnail-strip"
                        onWheel={onStripWheel}
                        onDrag={onStripWheel}
                    >
                        {mediaScans.map((scan, index) => {
                            const thumbnailState = thumbnailStates[scan.id];
                            const isSelected = index === selectedIndex;

                            return (
                                <button
                                    key={scan.id}
                                    ref={(node) => {
                                        if (node) {
                                            thumbnailRefs.current[scan.id] = node;
                                        } else {
                                            delete thumbnailRefs.current[scan.id];
                                        }
                                    }}
                                    type="button"
                                    data-scan-id={scan.id}
                                    className={`media-thumbnail-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setSelectedIndex(index)}
                                    aria-pressed={isSelected}
                                    title={scan.label}
                                >
                                    <div className="media-thumbnail-preview">
                                        {thumbnailState?.status === 'ready' ? (
                                            <img
                                                className="media-thumbnail-image"
                                                src={thumbnailState.src}
                                                alt={scan.label}
                                                draggable={false}
                                                onDragStart={preventImageDrag}
                                            />
                                        ) : thumbnailState?.status === 'failed' ? (
                                            <div className="media-thumbnail-fallback">
                                                <BrokenImageOutlinedIcon />
                                            </div>
                                        ) : (
                                            <div className="media-thumbnail-loading">
                                                <Spinner type="inline-bigger" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="media-thumbnail-name">{scan.label}</div>
                                </button>
                            );
                        })}
                    </div>

                    {!isStripCollapsed && stripNavigationState.canScrollRight && (
                        <button
                            type="button"
                            className="media-strip-nav next"
                            onClick={() => scrollStrip('next')}
                            aria-label="Show next media thumbnails"
                        >
                            <span className="media-strip-nav-fade" aria-hidden="true" />
                            <span className="media-strip-nav-icon" aria-hidden="true">
                                <ArrowForwardIosRoundedIcon />
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

MediaThumbnailStrip.propTypes = {
    mediaScans: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ).isRequired,
    selectedIndex: PropTypes.number.isRequired,
    setSelectedIndex: PropTypes.func.isRequired,
    setVisibleThumbnailIds: PropTypes.func.isRequired,
    thumbnailStates: PropTypes.objectOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            status: PropTypes.oneOf([
                'failed',
                'loading',
                'ready',
            ]).isRequired,
        }),
    ).isRequired,
};

export default MediaThumbnailStrip;
